import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from './useTheme';
import { SettingsService } from '../services/settingsService';
import { useAuth } from './useAuth';
import { canUsePreferencesStorage } from '../utils/storageConsent';
import { errorDev } from '../utils/logger';

export const useSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { theme, setTheme } = useTheme();
  const { i18n } = useTranslation();
  const { user } = useAuth();

  // `settings` and `theme` are mirrored in refs so rapid successive
  // updates read the latest confirmed value instead of a stale closure
  // snapshot. Without this, two quick toggles would both compute from
  // the same previous state and the second write could clobber the first.
  const settingsRef = useRef(null);
  const themeRef = useRef(theme);
  const savingCountRef = useRef(0);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  useEffect(() => {
    themeRef.current = theme;
  }, [theme]);

  const markSaving = useCallback((delta) => {
    savingCountRef.current = Math.max(0, savingCountRef.current + delta);
    setSaving(savingCountRef.current > 0);
  }, []);

  useEffect(() => {
    if (user) {
      loadSettings();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadSettings = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const userSettings = await SettingsService.getUserSettings(user.uid);
      setSettings(userSettings);
      settingsRef.current = userSettings;

      // Loading Settings must not change the active theme. Only an explicit
      // theme selection or reset action applies it.
      if (userSettings.language && userSettings.language !== i18n.language) {
        await i18n.changeLanguage(userSettings.language);
      }
    } catch (err) {
      errorDev('[Settings] Load failed:', err);
      setError(err);
      // Fallback to default settings
      const defaults = SettingsService.getDefaultSettings();
      setSettings(defaults);
      settingsRef.current = defaults;
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = useCallback(async (field, valueOrUpdater) => {
    const currentSettings = settingsRef.current;
    if (!user || !currentSettings) return;

    // Accept a plain value or an updater function. The updater form
    // resolves against the latest confirmed value, so two rapid
    // toggles of the same switch never compute from a stale render.
    const value =
      typeof valueOrUpdater === 'function'
        ? valueOrUpdater(currentSettings[field])
        : valueOrUpdater;

    const previousSettings = currentSettings;
    const previousTheme = themeRef.current;
    const updatedSettings = { ...currentSettings, [field]: value };
    // Apply optimistically and record the new value immediately so a
    // rapid follow-up toggle computes from this update, not the stale
    // pre-update state.
    setSettings(updatedSettings);
    settingsRef.current = updatedSettings;
    setError(null);

    markSaving(1);

    try {
      // Special handling for theme and language
      if (field === 'theme') {
        setTheme(value);
      }

      if (field === 'language') {
        await i18n.changeLanguage(value);
        if (canUsePreferencesStorage()) {
          localStorage.setItem('language', value);
        }
      }

      await SettingsService.updateUserSettings(user.uid, { [field]: value });
    } catch (err) {
      errorDev('[Settings] Update failed:', err);
      // Revert to the last confirmed value on failure so the control
      // always reconciles with what is actually stored.
      setSettings(previousSettings);
      settingsRef.current = previousSettings;
      if (field === 'theme') setTheme(previousTheme);
      setError(err);
      throw err;
    } finally {
      markSaving(-1);
    }
  }, [user, setTheme, i18n, markSaving]);

  const updateMultipleSettings = useCallback(async (updates) => {
    const currentSettings = settingsRef.current;
    if (!user || !currentSettings) return;

    const previousSettings = currentSettings;
    const previousTheme = themeRef.current;
    const updatedSettings = { ...currentSettings, ...updates };
    setSettings(updatedSettings);
    settingsRef.current = updatedSettings;
    setError(null);

    markSaving(1);

    try {
      // Handle special fields
      if (updates.theme) {
        setTheme(updates.theme);
      }

      if (updates.language) {
        await i18n.changeLanguage(updates.language);
        if (canUsePreferencesStorage()) {
          localStorage.setItem('language', updates.language);
        }
      }

      await SettingsService.updateUserSettings(user.uid, updates);
    } catch (err) {
      errorDev('[Settings] Multi-update failed:', err);
      setSettings(previousSettings);
      settingsRef.current = previousSettings;
      if (updates.theme) setTheme(previousTheme);
      setError(err);
      throw err;
    } finally {
      markSaving(-1);
    }
  }, [user, setTheme, i18n, markSaving]);

  const exportData = async () => {
    if (!user) throw new Error('No user signed in');
    
    try {
      setLoading(true);
      setError(null);
      const exportData = await SettingsService.exportUserData(user.uid);
      
      // Create and trigger download
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
        type: 'application/json' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orrith-data-${user.uid}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      return true;
    } catch (err) {
      errorDev('[Settings] Export failed:', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteAccount = async () => {
    if (!user) throw new Error('No user signed in');
    
    try {
      setError(null);
      return await SettingsService.deleteUserAccount(user.uid, user);
    } catch (err) {
      errorDev('[Settings] Delete account failed:', err);
      setError(err);
      throw err;
    }
  };

  const resetToDefaults = async () => {
    if (!user) return;

    const previousSettings = settingsRef.current;
    const previousTheme = themeRef.current;

    try {
      markSaving(1);
      setError(null);
      const defaultSettings = SettingsService.getDefaultSettings();
      setSettings(defaultSettings);
      settingsRef.current = defaultSettings;

      // Apply default theme and language
      setTheme(defaultSettings.theme);
      await i18n.changeLanguage(defaultSettings.language);

      await SettingsService.updateUserSettings(user.uid, defaultSettings);
    } catch (err) {
      errorDev('[Settings] Reset failed:', err);
      // Restore the previous settings so a failed reset does not
      // leave the UI showing values that were never persisted.
      if (previousSettings) {
        setSettings(previousSettings);
        settingsRef.current = previousSettings;
        if (previousTheme) setTheme(previousTheme);
      }
      setError(err);
      throw err;
    } finally {
      markSaving(-1);
    }
  };

  return {
    settings,
    loading,
    saving,
    error,
    updateSetting,
    updateMultipleSettings,
    exportData,
    deleteAccount,
    resetToDefaults,
    refreshSettings: loadSettings
  };
};